import {fetchAuth} from "../../../shared/api/ApiService"
import { ILessonTutor, ITutor, ITutorAccrual, ITutorChange, ITutorData, ITutorFinance, 
    ITutorPaymentDetail, ITutorReceiptItem, ITutorSalaryData, TTutorPaymentType } from "../model/types";
import { MyError } from "../../../shared/lib/error/MyError";
import {
    ITutorApi,
    ITutorListApi,
    ITutorListResponseApi,
    ITutorPaymentDetailApi,
    IReceiptsArchiveApi,
    ITutorSalaryResponseApi,
    ITutorSalaryRowApi,
} from "../model/apiTypes";

const mapTutorListItem = (tutor: ITutorListApi): ITutor => ({
    id: Number(tutor.userId),
    user_id: Number(tutor.userId),
    legacy_tutor_id: Number(tutor.legacyTutorId),
    full_name: tutor.fullName,
    tg: tutor.tg,
    email: tutor.email,
    is_archive: tutor.isArchive,
    labels: tutor.labels || [],
    desired_hours: tutor.desiredHours || '',
    actual_hours: tutor.actualHours || '',
})

const mapTutor = (tutor: ITutorApi): ITutorData => ({
    id: Number(tutor.userId),
    user_id: Number(tutor.userId),
    legacy_tutor_id: Number(tutor.legacyTutorId),
    full_name: tutor.fullName,
    tg: tutor.tg,
    email: tutor.email,
    phone: tutor.phone,
    tag_ids: (tutor.tagIds || []).map(Number),
    cost_per_hour: tutor.costPerHour,
    balance: tutor.balance,
    created_at: tutor.createdAt,
    work_tg_ids: (tutor.workTgIds || []).map(Number),
    is_archive: tutor.isArchive,
    archive_comment: tutor.archiveComment,
    labels: tutor.labels || [],
    desired_hours: tutor.desiredHours || '',
    actual_hours: tutor.actualHours || '',
    gender: tutor.gender || '',
    conversion: tutor.conversion,
    conversion_base: tutor.conversionBase ?? '0',
    conversion_closed_count: tutor.conversionClosedCount,
    conversion_lost_count: tutor.conversionLostCount,
    sanction_sequence_number: tutor.sanctionSequenceNumber ?? null,
})

const mapTutorPaymentDetail = (detail: ITutorPaymentDetailApi): ITutorPaymentDetail => ({
    id: Number(detail.id),
    type: detail.type,
    recipient_name: detail.recipientName,
    card_number: detail.cardNumber,
    phone_number: detail.phoneNumber,
    bank_name: detail.bankName,
    account_number: detail.accountNumber,
    bik: detail.bik,
    inn: detail.inn,
    is_priority: detail.isPriority,
})

const mapTutorPaymentDetailRequest = (detail: ITutorPaymentDetail) => ({
    id: detail.id,
    type: detail.type,
    recipientName: detail.recipient_name,
    cardNumber: detail.card_number,
    phoneNumber: detail.phone_number,
    bankName: detail.bank_name,
    accountNumber: detail.account_number,
    bik: detail.bik,
    inn: detail.inn,
    isPriority: detail.is_priority,
})

const mapTutorSalaryRow = (row: ITutorSalaryRowApi) => ({
    user_id: Number(row.userId),
    legacy_tutor_id: Number(row.legacyTutorId),
    full_name: row.fullName,
    tg_admin_name: row.tgAdminName,
    salary: row.salary,
    balance: row.balance,
    lessons_count: Number(row.lessonsCount || 0),
    hours: row.hours || '0',
    penalties: row.penalties || '0',
    bonuses: row.bonuses || '0',
    payment_type: row.paymentType,
    recipient_name: row.recipientName,
    card_number: row.cardNumber,
    phone_number: row.phoneNumber,
    bank_name: row.bankName,
    account_number: row.accountNumber,
    bik: row.bik,
    inn: row.inn,
})

const mapTutorListResponse = (data: ITutorListResponseApi) => {
    return {
        tutors: data.tutors.map(mapTutorListItem),
        tutors_count: Number(data.tutorsCount),
    }
}


class TutorService {

    controller: AbortController | null
    tutorRoleIdRequest: Promise<number> | null
    tutorRoleIdCache: number | null

    constructor() {
        this.controller = null;
        this.tutorRoleIdRequest = null;
        this.tutorRoleIdCache = null;
    }

    private async getTutorRoleId(): Promise<number> {
        if(this.tutorRoleIdCache){
            return this.tutorRoleIdCache
        }

        if(this.tutorRoleIdRequest){
            return this.tutorRoleIdRequest
        }

        const request = (async () => {
            const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/api/v1/idm/roles')
            const data: {roles: Array<{id: string; code: string}>} = await res.json()
            const tutorRole = data.roles.find(role => role.code === 'tutor')
            const tutorRoleId = tutorRole ? Number(tutorRole.id) : 0

            if(!tutorRoleId){
                throw new MyError('Не удалось определить роль исполнителя', 500)
            }

            this.tutorRoleIdCache = tutorRoleId
            return tutorRoleId
        })()

        this.tutorRoleIdRequest = request

        try{
            return await request
        }
        finally{
            this.tutorRoleIdRequest = null
        }
    }

    private async getTutorStaffList(work_tg_ids: number[] = []): Promise<{ tutors: ITutor[], tutors_count: number }> {
        const tutorRoleId = await this.getTutorRoleId()
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/api/v1/idm/staff', {
            method: 'POST',
            body: JSON.stringify({
                roleIds: [tutorRoleId],
                workTgIds: work_tg_ids,
            })
        })
        const data: {items: Array<{userId: string; fullName: string}>; total: string} = await res.json()

        const tutors = await Promise.all(
            data.items.map(async (item): Promise<ITutor> => {
                const userId = Number(item.userId)

                try{
                    const tutor = await this.getByUserId(userId)
                    return {
                        id: tutor.id,
                        user_id: tutor.user_id,
                        legacy_tutor_id: tutor.legacy_tutor_id,
                        full_name: tutor.full_name,
                        tg: tutor.tg,
                        email: tutor.email,
                        is_archive: tutor.is_archive,
                        work_tg_name: tutor.work_tg_name,
                    }
                }
                catch{
                    return {
                        id: userId,
                        user_id: userId,
                        full_name: item.fullName,
                        tg: '',
                    }
                }
            })
        )

        return {
            tutors,
            tutors_count: Number(data.total)
        }
    }

    async create(tutor: ITutorChange) {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/api/v1/tutors', {
            method: "POST",
            body: JSON.stringify({
                fullName: tutor.full_name,
                phone: tutor.phone,
                tg: tutor.tg,
                costPerHour: tutor.cost_per_hour,
                tagIds: tutor.tag_ids,
                email: tutor.email,
                workTgIds: tutor.work_tg_ids,
                desiredHours: tutor.desired_hours || '',
                gender: tutor.gender || '',
            })
        })
    }

    async delete(id: number) {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/tutors/' + id, {
            method: "DELETE"
        })
    }

    async deleteByUserId(userId: number) {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/tutors/user/${userId}`, {
            method: "DELETE"
        })
    }

    async getFinance(userId: number, from: string, to: string): Promise<ITutorFinance> {
        if (this.controller) {
            this.controller.abort()
        }
        this.controller = new AbortController()
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/tutors/user/${userId}/finance`, {
            method: "POST",
            body: JSON.stringify({from, to}),
            signal: this.controller.signal
        })
        const {data}: { data: ITutorFinance } = await res.json()
        this.controller = null;
        return data
    }

    async getFinanceByUserId(userId: number, from: string, to: string): Promise<ITutorFinance> {
        if (this.controller) {
            this.controller.abort()
        }
        this.controller = new AbortController()
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/tutors/user/${userId}/finance`, {
            method: "POST",
            body: JSON.stringify({from, to}),
            signal: this.controller.signal
        })
        const {data}: { data: ITutorFinance } = await res.json()
        this.controller = null;
        return data
    }

    async getAllByAdmin(id: number): Promise<{ tutors: ITutor[], tutors_count: number }> {
        void id
        return this.getTutorStaffList()
    }

    async get(id: number): Promise<ITutorData> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/tutors/' + id)
        const {tutor}: { tutor: ITutorData } = await res.json()
        return tutor
    }

    async getByUserId(userId: number): Promise<ITutorData> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/tutors/user/${userId}`)
        const data: {tutor: ITutorApi} = await res.json()
        return mapTutor(data.tutor)
    }

    async resetSanctionSequence(userId: number): Promise<void> {
        await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/tutors/user/${userId}/sanction-sequence/reset`, {
            method: 'POST',
            body: JSON.stringify({}),
        })
    }

    async updateSanctionSequence(userId: number, value: number): Promise<void> {
        await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/tutors/user/${userId}/sanction-sequence/update`, {
            method: 'POST',
            body: JSON.stringify({
                userId,
                sanctionSequenceNumber: value,
            }),
        })
    }

    async getAll(): Promise<{ tutors: ITutor[], tutors_count: number }> {
        return this.getAllV1()
    }

    async getAllV1(): Promise<{ tutors: ITutor[], tutors_count: number }> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/api/v1/tutors')
        return mapTutorListResponse(await res.json())
    }

    async trialLesson(student_id: number) {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/tutors/trial_lesson', {
            method: "POST",
            body: JSON.stringify({student_id})
        })
    }

    async getLessons(id: number, from: string, to: string): Promise<{lessons: ILessonTutor[], lessons_count: number}> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/tutors/' + id + '/lessons', {
            method: "POST",
            body: JSON.stringify({from, to})
        })
        const {lessons, lessons_count}: { lessons: ILessonTutor[], lessons_count: number } = await res.json()
        return {lessons, lessons_count}
    }

    async archive(id: number, comment: string): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/tutors/' + id + '/archive', {
            method: "POST",
            body: JSON.stringify({comment})
        })
    }

    async archiveByUserId(userId: number, comment: string): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/tutors/user/${userId}/archive`, {
            method: "POST",
            body: JSON.stringify({comment})
        })
    }

    async unarchive(id: number): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/tutors/' + id + '/unarchive', {
            method: "POST"
        })
    }

    async unarchiveByUserId(userId: number): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/tutors/user/${userId}/unarchive`, {
            method: "POST"
        })
    }

    async getArchiveAll(): Promise<{ tutors: ITutor[], tutors_count: number }> {
        return this.getArchiveAllV1()
    }

    async getArchiveAllV1(): Promise<{ tutors: ITutor[], tutors_count: number }> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/api/v1/tutors/archived')
        return mapTutorListResponse(await res.json())
    }

    async getAllByFilters(work_tg_ids: number[], is_failer = false, is_archive?: boolean, without_tg = false, labels: string[] = [], is_low_load = false, tagIds: number[] = []): Promise<{
        tutors: ITutor[],
        tutors_count: number
    }> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/api/v1/tutors/filter', {
            method: 'POST',
            body: JSON.stringify({
                workTgIds: work_tg_ids,
                isFailer: is_failer,
                isArchive: is_archive,
                withoutTg: without_tg,
                labels,
                isLowLoad: is_low_load,
                tagIds,
            })
        })
        return mapTutorListResponse(await res.json())
    }

    async getLabels() {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/api/v1/tutors/labels')
        const data = await res.json() as {labels?: Array<{text: string; background_color?: string; backgroundColor?: string; text_color?: string; textColor?: string}>}
        return (data.labels || []).map(label => ({
            text: label.text,
            background_color: label.background_color || label.backgroundColor || '',
            text_color: label.text_color || label.textColor || '',
        }))
    }

    async update(tutor: ITutorChange): Promise<void> {
        const userId = Number(tutor.user_id || tutor.id)

        if(!userId){
            throw new MyError('Не указан ID пользователя-исполнителя', 400)
        }

        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/tutors/user/${userId}/update`, {
            method: "POST",
            body: JSON.stringify({
                userId,
                fullName: tutor.full_name,
                phone: tutor.phone,
                email: tutor.email,
                tg: tutor.tg,
                costPerHour: tutor.cost_per_hour,
                tagIds: tutor.tag_ids,
                workTgIds: tutor.work_tg_ids,
                desiredHours: tutor.desired_hours || '',
                gender: tutor.gender || '',
            })
        })
    }

    async addTagToTutor(userId: number, tagId: number): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/tutors/user/${userId}/tags/add`, {
            method: 'POST',
            body: JSON.stringify({userId, tagId}),
        })
    }

    async removeTagFromTutor(userId: number, tagId: number): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/tutors/user/${userId}/tags/remove`, {
            method: 'POST',
            body: JSON.stringify({userId, tagId}),
        })
    }

    async addAccrual(tutorId: number, amount: string, comment: string) {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/billing/users/${tutorId}/payouts`, {
            method: "POST",
            body: JSON.stringify({amount, comment})
        })
    }

    async getAccruals(tutorId: number, from?: string, to?: string) {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/billing/users/${tutorId}/accruals`, {
            method: "POST",
            body: from && to ? JSON.stringify({from, to}) : undefined,
        })
        const {accruals}: { accruals: ITutorAccrual[] } = await res.json()
        return accruals
    }

    async getReceiptList(userId: number, from: string, to: string): Promise<ITutorReceiptItem[]> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/billing/users/${userId}/payouts/list`, {
            method: "POST",
            body: JSON.stringify({from, to}),
        })
        const {items}: {items: ITutorReceiptItem[]} = await res.json();
        return items
    }

    async listMyPayouts(): Promise<ITutorReceiptItem[]> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/api/v1/billing/payouts/my', {
            method: 'GET',
        })
        const data: {items?: ITutorReceiptItem[]; payouts?: ITutorReceiptItem[]} = await res.json()
        return data.items ?? data.payouts ?? []
    }

    async cancelPayout(userId: number, payoutId: string): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/billing/users/${userId}/payouts/${payoutId}/cancel`, {
            method: "POST",
            body: JSON.stringify({userId, payoutId}),
        })
    }

    async deleteReceipt(userId: number, payoutId: string): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/billing/users/${userId}/payouts/${payoutId}/receipt/delete`, {
            method: "POST",
            body: JSON.stringify({userId, payoutId}),
        })
    }

    async downloadReceipt(payoutId: string): Promise<{blob: Blob, fileName: string}> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/tutors/receipts/${payoutId}/download`, {
            method: "GET",
        }, false, true)
        const contentDisposition = res.headers.get('Content-Disposition') || ''
        const utfFileName = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
        const plainFileName = contentDisposition.match(/filename="?([^";]+)"?/i)?.[1]
        const blob = await res.blob()
        return {
            blob,
            fileName: decodeURIComponent(utfFileName || plainFileName || `receipt_${payoutId}.pdf`)
        }
    }

    async receiptSave(formData: FormData) {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/tutors/save_receipt', {
            method: "POST",
            body: formData
        }, false, true)
    }

    async getPendingReceipt(): Promise<ITutorReceiptItem | null> {
        try{
            const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/api/v1/billing/users/payouts/pending-receipt', {
                method: "GET"
            })
            const {item}: {item: ITutorReceiptItem} = await res.json()
            return item
        }
        catch(e){
            if(e instanceof MyError && e.status === 404){
                return null
            }
            throw e
        }
    }

    async receiptPending(): Promise<boolean> {
        return Boolean(await this.getPendingReceipt())
    }

    async getDownloadAllReceipts(from: string, to: string): Promise<File> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/api/v1/billing/receipts/download_all', {
            method: "POST",
            body: JSON.stringify({from, to}),
        })
        const data: IReceiptsArchiveApi = await res.json()
        const binary = atob(data.content)
        const bytes = new Uint8Array(binary.length)

        for(let i = 0; i < binary.length; i += 1){
            bytes[i] = binary.charCodeAt(i)
        }

        return new File(
            [bytes],
            data.fileName || 'receipts.zip',
            {type: data.contentType || 'application/zip'}
        )
    }

    async getPaymentDetails(tutorId: number): Promise<ITutorPaymentDetail[]> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/api/v1/tutors/user/' + tutorId + '/payment_details')
        const data: {paymentDetails: ITutorPaymentDetailApi[]} = await res.json()
        return data.paymentDetails.map(mapTutorPaymentDetail)
    }

    async savePaymentDetails(tutorId: number, paymentDetails: ITutorPaymentDetail[]): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/api/v1/tutors/user/' + tutorId + '/payment_details', {
            method: "POST",
            body: JSON.stringify({
                userId: tutorId,
                paymentDetails: paymentDetails.map(mapTutorPaymentDetailRequest),
            })
        })
    }

    async getSalaries(from: string, to: string, payment_type: TTutorPaymentType, work_tg_ids: number[]): Promise<ITutorSalaryData> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/api/v1/tutors/salaries', {
            method: "POST",
            body: JSON.stringify({
                from,
                to,
                paymentType: payment_type,
                workTgIds: work_tg_ids,
            })
        })
        const data: ITutorSalaryResponseApi = await res.json()

        return {
            items: data.items.map(mapTutorSalaryRow),
            total_salary: data.totalSalary,
        }
    }

    async downloadSalaries(from: string, to: string, payment_type: TTutorPaymentType, work_tg_ids: number[], user_ids: number[]): Promise<Blob> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/tutors/salaries/download', {
            method: "POST",
            body: JSON.stringify({
                from,
                to,
                payment_type,
                work_tg_ids,
                user_ids,
            }),
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            },
        }, false, true)
        return await res.blob()
    }

    async payoutSalaries(from: string, to: string, payment_type: TTutorPaymentType, work_tg_ids: number[], user_ids: number[]): Promise<number> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/tutors/salaries/payouts', {
            method: "POST",
            body: JSON.stringify({
                from,
                to,
                payment_type,
                work_tg_ids,
                user_ids,
            })
        })
        const {count}: {count: number} = await res.json()
        return count
    }

    async cancelLatestSalaryPayouts(): Promise<number> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/tutors/salaries/payouts/latest/cancel', {
            method: "POST",
            body: JSON.stringify({}),
        })
        const {count}: {count: number} = await res.json()
        return count
    }

}

export const tutorService = new TutorService()
